import { useMemo } from 'react';
import { Card, CardContent, CardHeader, ErrorBanner, Spinner } from '@/components/ui';
import { portfolioApi } from '@/services/portfolio-api';
import type { IPortfolioSummaryResponse, IProductTimeSeriesResponse } from '@skystone/shared';
import type { IMetricViewFilters } from '@/hooks/use-metric-view-filters';
import { useCardFetch } from '@/hooks/use-card-fetch';
import { DeltaLabel, formatWowText } from '@/components/shared/delta-label';
import { SummaryTile } from './SummaryTile';
import { issueTypeLabel } from './summary-layout';
import {
  rollingWeeklyAverage,
  wowDeltasByType,
  ISSUE_TYPES,
  type IWeeklyCounts,
} from '@/lib/metric-view/aggregation';
import { rollingAvgLabel } from '@/lib/metric-view/period-label';

interface IThroughputSummaryCardProps {
  filters: IMetricViewFilters;
}

const ROLLING_WEEKS = 4;

function pivotByType(res: IProductTimeSeriesResponse): IWeeklyCounts[] {
  return res.series.map((point) => ({
    week: point.period,
    Story: point.values.Story ?? 0,
    Bug: point.values.Bug ?? 0,
    Task: point.values.Task ?? 0,
  }));
}

interface IFetchResult {
  summary: IPortfolioSummaryResponse;
  trend: IProductTimeSeriesResponse;
}

export function ThroughputSummaryCard({ filters }: Readonly<IThroughputSummaryCardProps>) {
  const { data, loading, error } = useCardFetch<IFetchResult>(
    async () => {
      const [summary, trend] = await Promise.all([
        portfolioApi.getSummary({
          from: filters.from,
          to: filters.to,
          productId: filters.productId || undefined,
          teamId: filters.teamId || undefined,
          includeArchived: filters.includeArchived || undefined,
        }),
        portfolioApi.getThroughputTrend({
          from: filters.from,
          to: filters.to,
          granularity: 'weekly',
          groupBy: 'type',
          productId: filters.productId || undefined,
          teamId: filters.teamId || undefined,
          includeArchived: filters.includeArchived || undefined,
        }),
      ]);
      return { summary, trend };
    },
    [filters.from, filters.to, filters.productId, filters.teamId, filters.includeArchived],
    'Failed to load throughput summary.',
  );

  const weekly = useMemo(() => (data ? pivotByType(data.trend) : []), [data]);
  const rollingAvg = useMemo(() => rollingWeeklyAverage(weekly, ROLLING_WEEKS), [weekly]);
  const wow = useMemo(() => wowDeltasByType(weekly), [weekly]);

  const weeklyLabel = rollingAvgLabel(filters.from, filters.to, ROLLING_WEEKS);

  return (
    <Card>
      <CardHeader>
        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Throughput Summary</h3>
        <p className="text-[11px] text-muted-foreground">
          Total closed this period and {weeklyLabel.toLowerCase()} — higher is better.
        </p>
      </CardHeader>
      <CardContent>
        {loading && <div className="flex h-40 items-center justify-center"><Spinner /></div>}
        {!loading && error && <ErrorBanner message={error} />}
        {!loading && !error && data && (
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <SummaryTile
              value={data.summary.throughput.value.toLocaleString()}
              label="Total closed"
              delta={
                <DeltaLabel
                  direction={data.summary.throughput.trend?.direction ?? null}
                  text={data.summary.throughput.trend?.delta ?? '—'}
                  higherIsBetter
                />
              }
            />
            {ISSUE_TYPES.map((t) => (
              <SummaryTile
                key={t}
                value={rollingAvg[t].toLocaleString()}
                label={`${issueTypeLabel(t)} / week`}
                delta={
                  <DeltaLabel
                    direction={wow[t].direction}
                    text={`${formatWowText(wow[t].deltaValue)} WoW`}
                    higherIsBetter
                  />
                }
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
